import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Axios from "axios";
import { DATABASE_URL, USER_ROLES } from "../../appConstants";
import AuthHeader from "../../services/AuthHeader";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { format, formatDistanceToNow, addDays } from "date-fns";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useLoadingContext } from "../../hooks/useLoadingContext";
import { Loader } from "../../components/common";

const CustomToolbar = () => (
  <GridToolbarContainer>
    <div
      style={{
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        alignItems: "center",
        padding: "0 10px",
      }}
    >
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </div>
  </GridToolbarContainer>
);

function PendingForGrading() {
  const { user } = useAuthContext();
  const [gradingList, setGradingList] = useState([]);
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(10);
  const { isLoading, startLoading, stopLoading } = useLoadingContext();
  const [refresh, setRefresh] = useState(false);
  const [selectedGrading, setSelectedGrading] = useState(null);
  const [examiners, setExaminers] = useState([]);
  const [examinerId, setExaminerId] = useState("");
  const [isLoadingExaminers, setIsLoadingExaminers] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);
  const [reassignmentError, setReassignmentError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const editGrading = useCallback(
    (id) => () => {
      navigate(id.toString());
    },
    [navigate]
  );

  const closeReassignment = () => {
    if (isReassigning) return;
    setSelectedGrading(null);
    setExaminers([]);
    setExaminerId("");
    setReassignmentError("");
  };

  const openReassignment = (grading) => async () => {
    setSelectedGrading(grading);
    setExaminers([]);
    setExaminerId("");
    setReassignmentError("");
    setSuccessMessage("");
    setIsLoadingExaminers(true);

    try {
      const response = await Axios.get(
        `${DATABASE_URL}/grading/${grading.id}/examiners`,
        { headers: AuthHeader() }
      );
      setExaminers(response.data.examiners);
      if (response.data.examiners.length === 0) {
        setReassignmentError("No other active compatible examiners are available.");
      }
    } catch (error) {
      setReassignmentError(
        (error.response && error.response.data && error.response.data.error) ||
          "Could not load available examiners."
      );
    } finally {
      setIsLoadingExaminers(false);
    }
  };

  const reassignPendingResponses = async () => {
    if (!selectedGrading || !examinerId) return;

    setIsReassigning(true);
    setReassignmentError("");
    try {
      const response = await Axios.put(
        `${DATABASE_URL}/grading/${selectedGrading.id}/reassign`,
        { examiner_id: examinerId },
        { headers: AuthHeader() }
      );
      setSuccessMessage(response.data.message);
      setSelectedGrading(null);
      setExaminers([]);
      setExaminerId("");
      setRefresh((value) => !value);
    } catch (error) {
      setReassignmentError(
        (error.response && error.response.data && error.response.data.error) ||
          "Could not reassign pending responses."
      );
    } finally {
      setIsReassigning(false);
    }
  };

  const columns = [
    { field: "answer_time", headerName: "Test Date", flex: 1 },
    { field: "id", headerName: "Test ID", flex: 1 },
    { field: "candidate_id", headerName: "Candidate ID", flex: 1 },
    { field: "candidate_full_name", headerName: "Candidate", flex: 1 },
    { field: "examiner_name", headerName: "Examiner", flex: 1 },
    { field: "deadline", headerName: "Deadline", flex: 1 },
    { field: "question_type", headerName: "Question Type", flex: 1 },
    {
      field: "actions",
      type: "actions",
      flex: 1.5,
      sortable: false,
      filterable: false,
      getActions: (params) => {
        const actions = [
          <Button key="view" variant="outlined" onClick={editGrading(params.id)}>
            {user.role === USER_ROLES.Administrator ? "View" : "Grade"}
          </Button>,
        ];

        if (user.role === USER_ROLES.Administrator) {
          actions.push(
            <Button
              key="reassign"
              variant="outlined"
              onClick={openReassignment(params.row)}
            >
              Reassign
            </Button>
          );
        }

        return actions;
      },
    },
  ];

  useEffect(() => {
    startLoading();

    Axios.get(
      DATABASE_URL +
        "/grading/test_center/" +
        user.test_center +
        "/examiner/" +
        user.userId,
      { headers: AuthHeader() }
    )
      .then((response) => {
        const filteredList = response.data.grading.map((item) => {
          const testDate = new Date(item.answer_time);
          let dateOffset = 5;
          if (testDate.getDay() > 1) {
            dateOffset = 7;
          }

          return {
            ...item,
            deadline: formatDistanceToNow(addDays(testDate, dateOffset), {
              addSuffix: true,
            }),
            answer_time: format(testDate, "dd/MM/yyyy"),
          };
        });

        setGradingList(filteredList);
        stopLoading();
      })
      .catch((error) => {
        console.log("Error: ", error);
        stopLoading();
      });
  }, [refresh]);

  return (
    <div>
      <h1>Pending For Grading</h1>
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}
      <div style={{ height: 600, width: "100%" }}>
        {isLoading ? (
          <div className="loader-display">
            <Loader />
          </div>
        ) : (
          <DataGrid
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            initialState={{
              sorting: {
                sortModel: [{ field: "deadline", sort: "asc" }],
              },
            }}
            components={{
              Toolbar: CustomToolbar,
            }}
            rows={gradingList}
            columns={columns}
          />
        )}
      </div>

      <Dialog
        open={Boolean(selectedGrading)}
        onClose={closeReassignment}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reassign Pending Responses</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            All pending {selectedGrading && selectedGrading.question_type} responses for
            candidate {selectedGrading && selectedGrading.candidate_id} will move to the
            selected examiner.
            Graded responses will not change.
          </DialogContentText>

          {reassignmentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reassignmentError}
            </Alert>
          )}

          {isLoadingExaminers ? (
            <CircularProgress size="1.5rem" />
          ) : (
            <FormControl fullWidth disabled={examiners.length === 0}>
              <InputLabel id="reassign-examiner-label">Examiner</InputLabel>
              <Select
                labelId="reassign-examiner-label"
                value={examinerId}
                label="Examiner"
                onChange={(event) => setExaminerId(event.target.value)}
              >
                {examiners.map((examiner) => (
                  <MenuItem key={examiner.id} value={examiner.id}>
                    {examiner.first_name} {examiner.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReassignment} disabled={isReassigning}>
            Cancel
          </Button>
          <Button
            onClick={reassignPendingResponses}
            variant="contained"
            disabled={!examinerId || isLoadingExaminers || isReassigning}
          >
            {isReassigning ? "Reassigning..." : "Reassign"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PendingForGrading;
